import type { PickedLocation } from "./types";

export type AddressSuggestion = {
	label: string;
	lat: number;
	lng: number;
};

export type AdministrativeSuggestion = {
	value: string;
	label: string;
};

type NominatimAddressDetails = {
	state?: string;
	region?: string;
	province?: string;
	county?: string;
	city?: string;
	town?: string;
	village?: string;
	municipality?: string;
	country?: string;
};

type NominatimAdministrativeResult = {
	display_name?: string;
	address?: NominatimAddressDetails;
};

const normalizeToken = (value: string) =>
	value
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/\s+/g, " ")
		.trim();

const firstNonEmpty = (...values: Array<string | null | undefined>) => {
	for (const value of values) {
		if (typeof value !== "string") continue;
		const cleanValue = value.trim();
		if (cleanValue) return cleanValue;
	}
	return "";
};

export async function reverseGeocodeNominatim(
	coords: PickedLocation,
	signal?: AbortSignal,
): Promise<string | null> {
	const { lat, lng } = coords;
	const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}`;
	const res = await fetch(url, {
		signal,
		headers: { Accept: "application/json", "Accept-Language": "es" },
	});
	if (!res.ok) return null;
	const data = (await res.json()) as { display_name?: string };
	const displayName =
		typeof data.display_name === "string" ? data.display_name.trim() : "";
	return displayName || null;
}

export async function searchAddressNominatim(
	query: string,
	signal?: AbortSignal,
): Promise<AddressSuggestion[]> {
	const q = query.trim();
	if (!q) return [];
	const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&q=${encodeURIComponent(q)}`;
	const res = await fetch(url, {
		signal,
		headers: { Accept: "application/json", "Accept-Language": "es" },
	});
	if (!res.ok) return [];
	const data = (await res.json()) as Array<{
		display_name?: string;
		lat?: string;
		lon?: string;
	}>;
	if (!Array.isArray(data)) return [];
	return data
		.map((item) => {
			const label =
				typeof item.display_name === "string" ? item.display_name.trim() : "";
			const lat = Number(item.lat);
			const lng = Number(item.lon);
			if (!label || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
			return { label, lat, lng };
		})
		.filter((x): x is AddressSuggestion => x !== null);
}

export async function searchAdministrativeNominatim(
	query: string,
	options: {
		level: "state" | "city";
		stateContext?: string;
		country?: string;
		limit?: number;
		signal?: AbortSignal;
	},
): Promise<AdministrativeSuggestion[]> {
	const q = query.trim();
	if (!q) return [];

	const {
		level,
		stateContext,
		country = "Venezuela",
		limit = 7,
		signal,
	} = options;

	const queryParts = [q];
	if (level === "city" && stateContext?.trim()) {
		queryParts.push(stateContext.trim());
	}
	if (country.trim()) {
		queryParts.push(country.trim());
	}

	const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=${encodeURIComponent(limit)}&q=${encodeURIComponent(queryParts.join(", "))}`;
	const res = await fetch(url, {
		signal,
		headers: { Accept: "application/json", "Accept-Language": "es" },
	});
	if (!res.ok) return [];

	const data = (await res.json()) as NominatimAdministrativeResult[];
	if (!Array.isArray(data)) return [];

	const normalizedQuery = normalizeToken(q);
	const rawSuggestions = data
		.map((item) => {
			const address = item.address ?? {};
			const stateName = firstNonEmpty(
				address.state,
				address.region,
				address.province,
				address.county,
			);
			const cityName = firstNonEmpty(
				address.city,
				address.town,
				address.village,
				address.municipality,
				address.county,
			);
			const fallback = firstNonEmpty(item.display_name?.split(",")[0]);

			const value = level === "state" ? stateName || fallback : cityName || fallback;
			if (!value) return null;

			const label =
				level === "state"
					? [value, firstNonEmpty(address.country, country)].filter(Boolean).join(", ")
					: [value, stateName].filter(Boolean).join(", ");

			const normalizedValue = normalizeToken(value);
			const normalizedLabel = normalizeToken(label);
			if (
				normalizedQuery &&
				!normalizedValue.includes(normalizedQuery) &&
				!normalizedLabel.includes(normalizedQuery)
			) {
				return null;
			}

			return { value, label };
		})
		.filter((suggestion): suggestion is AdministrativeSuggestion => suggestion !== null);

	const uniqueSuggestions: AdministrativeSuggestion[] = [];
	const seen = new Set<string>();
	for (const suggestion of rawSuggestions) {
		const key = normalizeToken(suggestion.value);
		if (!key || seen.has(key)) continue;
		seen.add(key);
		uniqueSuggestions.push(suggestion);
	}

	return uniqueSuggestions.slice(0, Math.max(1, limit));
}
